import { AUTH_RECOVERY_ELIGIBLE, type AuthRecoveryRequestInit } from '@/lib/fetch-request-context';

// 'handled' = 앱이 스스로 잡아 중단시킨 오류. 자동 계측(fetch·xhr·window.error)이 절대 못 보는 종류다 —
// 잘못된 입력을 막으면 요청이 안 나가므로 4xx 도 안 생기고, 그러면 관측도 같이 사라진다.
// 막되 보이게 하려면 호출부가 명시적으로 보고해야 한다 — 반드시 아래 `reportHandledError()` 를 쓴다
// (직접 `source: 'handled'` 를 넘기면 메시지 접두를 빠뜨려 게이트웨이 치환 후 구분이 사라진다).
// ⚠️ 리터럴은 lawkit-react 와 동일해야 한다 — 다르면 레포 간 집계가 갈린다.
type ErrorSource = 'window.error' | 'unhandledrejection' | 'react-boundary' | 'fetch' | 'xhr' | 'handled';

type RequestContext = {
  method?: string;
  path?: string;
  status?: number;
  requestId?: string;
};

type ReportContext = {
  source: ErrorSource;
  componentStack?: string;
  digest?: string;
  request?: RequestContext;
};

type Breadcrumb = {
  time: string;
  type: 'fetch' | 'xhr';
  method: string;
  path: string;
  status: number;
};

type WirePayload = {
  occurredAt: string;
  release: string;
  source: ErrorSource;
  error: { name: string; message: string; stack: string; componentStack: string; digest: string };
  page: { path: string };
  request: RequestContext;
  client: { language: string; viewport: string };
  breadcrumbs: Breadcrumb[];
};

const breadcrumbs: Breadcrumb[] = [];
const recentReports = new Map<string, number>();
let installed = false;
let pageLeftForeground = false;
let nativeFetch: typeof window.fetch | undefined;
let sentThisSession = 0;

const MAX_REPORTS_PER_SESSION = 30;
const MAX_PAYLOAD_BYTES = 28 * 1024;
const FIRST_PARTY_BASE_DOMAINS = ['revieworks.com', 'reviewed.co.kr', 'lawkit.kr', 'medilawyer.co.kr'];

const release = process.env.NEXT_PUBLIC_RELEASE || 'unknown';
const explicitEndpoint = process.env.NEXT_PUBLIC_CLIENT_ERROR_ENDPOINT || '';

function endpointFor(hostname: string): string {
  if (explicitEndpoint) return explicitEndpoint;
  const host = hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') return '';
  const gatewayLabel = host.split('.').some((label) => ['dev', 'stage', 'staging', 'imsi', 'next'].includes(label)) ? 'gateway.dev' : 'gateway';
  if (host === 'contents.revieworks.com' || host.endsWith('.contents.revieworks.com')) {
    return `https://${gatewayLabel}.revieworks.com/client-errors`;
  }
  if (host === 'revieworks.com' || host.endsWith('.revieworks.com')) {
    return `https://${gatewayLabel}.revieworks.com/client-errors`;
  }
  if (host === 'reviewed.co.kr' || host.endsWith('.reviewed.co.kr')) {
    return `https://${gatewayLabel}.reviewed.co.kr/client-errors`;
  }
  if (host === 'lawkit.kr' || host.endsWith('.lawkit.kr')) {
    return `https://${gatewayLabel}.lawkit.kr/client-errors`;
  }
  if (host === 'medilawyer.co.kr' || host.endsWith('.medilawyer.co.kr')) {
    return `https://${gatewayLabel}.medilawyer.co.kr/client-errors`;
  }
  return '';
}

function pathOnly(value: string | URL): string {
  try {
    return new URL(String(value), window.location.origin).pathname || '/';
  } catch {
    return String(value).split(/[?#]/, 1)[0].slice(0, 500);
  }
}

function isFirstPartyRequest(value: string | URL): boolean {
  try {
    const url = new URL(String(value), window.location.origin);
    if (url.origin === window.location.origin) return true;
    const host = url.hostname.toLowerCase();
    return FIRST_PARTY_BASE_DOMAINS.some((base) => host === base || host.endsWith(`.${base}`));
  } catch {
    return false;
  }
}

function isHttpError(status: number): boolean {
  return status >= 400 && status <= 599;
}

// next-auth SessionProvider 는 모든 페이지에서 로그인 여부를 확인한다.
// 비로그인 방문자에게 돌아오는 401/403 은 "로그인 상태 아님" 그 자체이고,
// next-auth 클라이언트가 session=null 로 정상 처리하므로 앱 오류가 아니다.
//
// ⚠️ 여기에 `/api/auth/csrf` · `/api/auth/providers` 를 넣으면 안 된다.
// 두 경로의 401/403 은 "비로그인" 이 아니라 **로그인 시작 자체가 막힌 장애**다
// (signIn() 이 provider 목록·csrf 토큰을 못 받아 소셜 로그인이 시작되지 않는다).
// 설치된 @auth/core 는 세 경로 모두 정상 응답이 200 이므로, session 외의 401/403 은
// 반드시 티켓이 되어야 한다.
const AUTH_STATE_PROBE_PATHS = new Set(['/api/auth/session']);

function isUnauthenticatedProbe(path: string, status: number): boolean {
  return (status === 401 || status === 403) && AUTH_STATE_PROBE_PATHS.has(path);
}

function isRecoverableAuthResponse(status: number, init?: RequestInit): boolean {
  return status === 401 && Boolean((init as AuthRecoveryRequestInit | undefined)?.[AUTH_RECOVERY_ELIGIBLE]);
}

/**
 * XHR 의 401 은 Axios 응답 인터셉터가 재발급·재시도까지 끝낸 뒤 최종 실패만 보고한다.
 * XHR loadend 는 인터셉터보다 먼저 실행되므로 여기서 보고하면 정상 복구도 장애로 잡힌다.
 * breadcrumb 는 아래에서 먼저 남기므로 복구 실패 보고의 직전 정황은 유지된다.
 *
 * 현재 앱의 first-party XHR 호출은 모두 공통 Axios 인스턴스를 사용한다. raw XHR 호출을
 * 새로 추가한다면 그 호출부가 최종 401 보고 책임도 함께 가져야 한다.
 */
function isAxiosManagedAuthResponse(status: number): boolean {
  return status === 401;
}

function safeErrorText(value: string, limit: number): string {
  const withoutUrlQueries = value.replace(/https?:\/\/[^\s)\]}]+/g, (candidate) => {
    try {
      const url = new URL(candidate);
      url.search = '';
      url.hash = '';
      return url.toString();
    } catch {
      return candidate.split(/[?#]/, 1)[0];
    }
  });
  const withoutRelativeUrlQueries = withoutUrlQueries.replace(/((?:\.{0,2}\/)[^\s?#)\]}]+)[?#][^\s)\]}]*/g, '$1');
  return withoutRelativeUrlQueries
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[REDACTED_EMAIL]')
    .replace(/(?:\+?82[- ]?)?0?1[016789][-. ]?\d{3,4}[-. ]?\d{4}/g, '[REDACTED_PHONE]')
    .replace(/\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]{8,}/gi, '$1 [REDACTED_TOKEN]')
    .slice(0, limit);
}

function errorFrom(value: unknown): Error {
  if (value instanceof Error) return value;
  if (typeof value === 'string') return new Error(value.slice(0, 1_500));
  return new Error(`Unhandled rejection (${Object.prototype.toString.call(value)})`);
}

function isNoise(error: Error): boolean {
  return error.name === 'AbortError' || /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i.test(error.message) || /^Script error\.?$/i.test(error.message);
}

/**
 * 브라우저가 요청을 끊었을 때 fetch 가 내는 메시지들. 이탈 중일 때만 억제 근거로 쓴다.
 *
 * 문구만으로 버리지 않는다 — `isNoise` 에서 `Load failed` 를 걷어낸 것이 그 이유다.
 * `Load failed` 는 사파리가 fetch 실패에 쓰는 표준 문구라(오프라인·DNS·서버다운·CORS)
 * 통째로 버리면 사파리 사용자의 진짜 장애가 한 건도 안 올라온다.
 * 여기서는 **이탈 중** 이라는 사실과 **함께** 쓰이므로 평상시에는 그대로 수집된다.
 *
 * 이 목록은 최선의 근사치이지 완전하지 않다. 빠진 문구는 "이탈 중인데도 수집된다"로
 * 나타난다 — 안전한 쪽으로 틀린다.
 */
const FETCH_TRANSPORT_FAILURE_MESSAGE = /^(Load failed|Failed to fetch|NetworkError when attempting to fetch resource\.?|The Internet connection appears to be offline\.?|cancelled|canceled)$/i;

/**
 * axios 는 XHR 실패를 **새 AxiosError 로 감싸서** 거절한다. 그래서 XHR 계측이 억제해도
 * 그 거절은 별개의 객체로 `unhandledrejection` 에 도착한다 — 동일성으로는 못 잇는다.
 * 대신 axios 가 붙이는 코드로 판별한다(`axios/lib/adapters/xhr.js` 의 거절 지점 세 곳).
 *
 * ⚠️ 기본 설정(`clarifyTimeoutError` 꺼짐)에서 **타임아웃과 중단이 같은 `ECONNABORTED`** 다.
 * 타임아웃은 이탈로 뭉개면 안 되므로(백그라운드 탭에서도 진짜 타임아웃이 난다)
 * 문구로 한 번 더 갈라낸다. `ETIMEDOUT` 은 아예 억제 대상이 아니다.
 */
const AXIOS_CANCELLABLE_CODES = new Set(['ECONNABORTED', 'ERR_NETWORK']);
const AXIOS_TIMEOUT_MESSAGE = /timeout/i;

function isAxiosTransportCancellation(error: Error): boolean {
  const candidate = error as Error & { isAxiosError?: boolean; code?: string; response?: unknown };
  if (candidate.isAxiosError !== true) return false;
  // 서버가 응답했으면 transport 실패가 아니다.
  if (candidate.response) return false;
  if (!AXIOS_CANCELLABLE_CODES.has(candidate.code ?? '')) return false;
  // ECONNABORTED 로 뭉뚱그려진 타임아웃을 여기서 되살린다.
  return !AXIOS_TIMEOUT_MESSAGE.test(error.message);
}

/**
 * fetch 계측이 **이미 판정을 끝낸** 오류들.
 *
 * fetch monitor 는 실패를 보고하거나 억제한 뒤 **같은 객체를 그대로 다시 던진다**(재던지지 않으면
 * 호출부의 catch 가 사라진다). 앱이 그 거절을 안 받으면 `unhandledrejection` 으로 올라와
 * **같은 실패가 두 번 집계된다** — `source` 가 달라 중복 억제(`shouldSend`)에도 안 걸린다.
 *
 * 예전에는 `isNoise` 가 `Load failed` 를 통째로 버려 사파리 쪽 두 번째 사본이 우연히 죽었다.
 * 그 필터를 걷어낸 이상 여기서 명시적으로 끊는다. 같은 객체가 다시 던져지므로 동일성으로 이어진다.
 */
const settledByFetchMonitor = new WeakSet<object>();

function markSettledByFetchMonitor(value: unknown): void {
  if (value !== null && (typeof value === 'object' || typeof value === 'function')) {
    settledByFetchMonitor.add(value as object);
  }
}

function wasSettledByFetchMonitor(value: unknown): boolean {
  return value !== null && (typeof value === 'object' || typeof value === 'function') && settledByFetchMonitor.has(value as object);
}

// 페이지 이탈(pagehide)·백그라운드 전환(visibilitychange) 구간에서는 브라우저가
// 진행 중인 요청을 스스로 끊는다. 이때 남는 status 0은 서버 응답이 아니라 취소이며,
// fetch monitor가 AbortError를 제외하는 것과 같은 이유로 앱 오류로 볼 수 없다.
//
// ⚠️ XHR 의 `abort` 이벤트만으로는 이걸 못 잡는다 — 탭을 닫으면 브라우저가 연결을
// 스스로 끊을 뿐 `xhr.abort()` 가 불리지 않아 abort 리스너가 안 돈다.
export function isBrowserCancelledRequest(status: number): boolean {
  if (status !== 0) return false;
  try {
    return pageLeftForeground || document.visibilityState === 'hidden';
  } catch {
    return false;
  }
}

export function installPageLifecycleMonitor(): void {
  window.addEventListener('pagehide', () => {
    pageLeftForeground = true;
  });
  // bfcache 복귀·재활성화는 이탈이 아니므로 다시 수집한다.
  window.addEventListener('pageshow', () => {
    pageLeftForeground = false;
  });
  document.addEventListener('visibilitychange', () => {
    pageLeftForeground = document.visibilityState === 'hidden';
  });
}

/**
 * 이탈 중에 흘러나온 transport 실패인가.
 *
 * 억제하는 것은 **이탈 중 · 상태코드 없음 · transport 실패** 세 조건이 모두 맞을 때뿐이다.
 */
export function isCancelledTransportReport(error: Error, context: ReportContext): boolean {
  // 상태코드가 있으면 서버가 응답한 것이다. 이탈과 무관한 진짜 신호이므로 절대 억제하지 않는다.
  if (context.request?.status) return false;
  if (!FETCH_TRANSPORT_FAILURE_MESSAGE.test(error.message) && !isAxiosTransportCancellation(error)) {
    return false;
  }
  return isBrowserCancelledRequest(0);
}

function remember(type: Breadcrumb['type'], request: RequestContext): void {
  breadcrumbs.push({
    time: new Date().toISOString(),
    type,
    method: (request.method || 'GET').slice(0, 12),
    path: pathOnly(request.path || '/'),
    status: request.status || 0
  });
  if (breadcrumbs.length > 20) breadcrumbs.splice(0, breadcrumbs.length - 20);
}

function responseRequestId(headers: Headers): string {
  return headers.get('x-request-id') || headers.get('x-amzn-requestid') || headers.get('trace-id') || '';
}

function shouldSend(error: Error, context: ReportContext): boolean {
  if (sentThisSession >= MAX_REPORTS_PER_SESSION) return false;
  const firstFrame = (error.stack || '').split('\n')[1] || '';
  const key = [context.source, error.name, error.message, firstFrame, context.request?.path, context.request?.status].join('|');
  const now = Date.now();
  const previous = recentReports.get(key) || 0;
  recentReports.set(key, now);
  if (recentReports.size > 100) {
    recentReports.forEach((time, candidate) => {
      if (now - time > 60_000) recentReports.delete(candidate);
    });
    while (recentReports.size > 100) {
      const oldest = recentReports.keys().next().value;
      if (oldest === undefined) break;
      recentReports.delete(oldest);
    }
  }
  if (now - previous <= 5_000) return false;
  sentThisSession += 1;
  return true;
}

function serializeWithinLimit(payload: WirePayload): string {
  let body = JSON.stringify(payload);
  if (new Blob([body]).size <= MAX_PAYLOAD_BYTES) return body;

  for (const [stack, componentStack, crumbCount] of [
    [6_000, 2_500, 10],
    [3_000, 1_200, 5],
    [1_000, 400, 2],
    [0, 0, 0]
  ] as const) {
    body = JSON.stringify({
      ...payload,
      error: {
        ...payload.error,
        message: stack === 0 ? payload.error.message.slice(0, 500) : payload.error.message,
        stack: payload.error.stack.slice(0, stack),
        componentStack: payload.error.componentStack.slice(0, componentStack)
      },
      breadcrumbs: crumbCount === 0 ? [] : payload.breadcrumbs.slice(-crumbCount)
    });
    if (new Blob([body]).size <= MAX_PAYLOAD_BYTES) return body;
  }
  return '';
}

function send(payload: WirePayload): void {
  const endpoint = endpointFor(window.location.hostname);
  if (!endpoint) return;
  const body = serializeWithinLimit(payload);
  if (!body) return;
  try {
    if (navigator.sendBeacon?.(endpoint, body)) return;
  } catch {
    // fetch keepalive fallback below
  }
  const sendFetch = nativeFetch || window.fetch.bind(window);
  try {
    void Promise.resolve(
      sendFetch(endpoint, {
        method: 'POST',
        body,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        keepalive: true,
        credentials: 'omit',
        mode: 'cors'
      })
    ).catch(() => undefined);
  } catch {
    // 수집 실패는 원래 사용자 흐름에 절대 전파하지 않는다.
  }
}

export function reportFrontendError(value: unknown, context: ReportContext): void {
  try {
    if (typeof window === 'undefined') return;
    const error = errorFrom(value);
    if (isNoise(error) || isCancelledTransportReport(error, context) || !shouldSend(error, context)) return;
    const digest = context.digest || (error as Error & { digest?: string }).digest || '';
    send({
      occurredAt: new Date().toISOString(),
      release,
      source: context.source,
      error: {
        name: error.name || 'Error',
        message: safeErrorText(error.message || 'No message provided.', 1_500),
        stack: safeErrorText(error.stack || '', 12_000),
        componentStack: safeErrorText(context.componentStack || '', 6_000),
        digest: safeErrorText(digest, 160)
      },
      page: { path: window.location.pathname },
      request: context.request || {},
      client: {
        language: navigator.language || '',
        viewport: `${window.innerWidth}x${window.innerHeight}`
      },
      breadcrumbs: breadcrumbs.slice(-20)
    });
  } catch {
    // 오류 reporter 자체가 애플리케이션 오류가 되어서는 안 된다.
  }
}

/**
 * 앱이 스스로 막은 오류를 올린다. **`source: 'handled'` 보고는 반드시 이 함수를 거친다.**
 * 직접 `reportFrontendError({ source: 'handled' })` 를 부르지 말 것 — source 를 한 곳에 모아 둔다.
 *
 * ⚠️ **`message` 에 가변값을 넣지 말 것.** 게이트웨이가
 * `fingerprint = sha256(service|release|name|normalizedMessage|digest|culprit)` 로 케이스를 묶는데,
 * 메시지가 값마다 다르면 **같은 사건이 큐에서 케이스로 쪼개진다**(정규화는 UUID 와 3자리 이상 숫자뿐).
 * 가변값은 `request.path` 로 넘긴다 — **fingerprint 에 들어가지 않으면서** 이벤트에는 보존된다.
 * 현재 화면 주소는 리포터가 `page.path` 로 이미 싣고 있으므로 따로 넣을 필요가 없다.
 *
 * 전제: 게이트웨이 화이트리스트에 `'handled'` 가 있어야 한다(`legalcare-gateway#51`).
 * 없으면 조용히 `window.error` 로 치환된다 — 이 PR 은 `#51` 배포 이후에 나가야 한다.
 */
export function reportHandledError(message: string, request?: RequestContext): void {
  reportFrontendError(new Error(message), {
    source: 'handled',
    request
  });
}

function installFetchMonitor(): void {
  nativeFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    let method = 'GET';
    let path = '';
    let firstParty = false;
    try {
      const requestUrl = input instanceof Request ? input.url : String(input);
      method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
      path = pathOnly(requestUrl);
      firstParty = isFirstPartyRequest(requestUrl);
    } catch {
      // 메타데이터 추출 실패와 무관하게 원래 fetch는 그대로 실행한다.
    }
    try {
      const response = await nativeFetch!(input, init);
      try {
        if (firstParty) {
          const request = { method, path, status: response.status, requestId: responseRequestId(response.headers) };
          remember('fetch', request);
          if (isHttpError(response.status) && !isUnauthenticatedProbe(path, response.status) && !isRecoverableAuthResponse(response.status, init)) {
            reportFrontendError(new Error(`${method} ${path} failed with HTTP ${response.status}`), {
              source: 'fetch',
              request
            });
          }
        }
      } catch {
        // 원래 fetch response를 보존한다.
      }
      return response;
    } catch (failure) {
      // 보고했든 억제했든 **판정은 여기서 끝났다.** 아래에서 그대로 다시 던지므로,
      // 표시해 두지 않으면 전역 unhandledrejection 이 같은 실패를 한 번 더 집계한다.
      if (firstParty) markSettledByFetchMonitor(failure);

      if (firstParty && !(failure instanceof DOMException && failure.name === 'AbortError')) {
        try {
          const request = { method, path, status: 0 };
          // 억제하더라도 breadcrumb 은 남긴다 — 뒤에 진짜 오류가 나면 그 직전 정황이 된다.
          // (이식원 reviewed-react 도 remember 뒤에 억제를 판정한다.)
          remember('fetch', request);
          if (!isBrowserCancelledRequest(request.status)) {
            reportFrontendError(failure, { source: 'fetch', request });
          }
        } catch {
          // 원래 fetch rejection을 보존한다.
        }
      }
      throw failure;
    }
  };
}

function installXhrMonitor(): void {
  // 3상태다. `abort` 만으로는 부족하다 — 탭을 닫으면 abort() 가 안 불리고,
  // `timeout` 은 브라우저가 끊은 게 아니라 서버가 제때 답하지 않은 것이라 보고해야 한다.
  type XhrMeta = { method: string; path: string; ending: '' | 'abort' | 'timeout' };
  const metadata = new WeakMap<XMLHttpRequest, XhrMeta>();
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function monitoredOpen(method: string, url: string | URL, async: boolean = true, username?: string | null, password?: string | null): void {
    originalOpen.call(this, method, url, async, username, password);
    metadata.delete(this);
    try {
      if (isFirstPartyRequest(url)) {
        metadata.set(this, { method: method.toUpperCase(), path: pathOnly(url), ending: '' });
      }
    } catch {
      // 원래 XHR open 성공 여부에 영향을 주지 않는다.
    }
  };

  XMLHttpRequest.prototype.send = function monitoredSend(body?: Document | XMLHttpRequestBodyInit | null): void {
    const meta = metadata.get(this);
    if (meta) {
      try {
        this.addEventListener('abort', () => {
          meta.ending = 'abort';
        });
        this.addEventListener('timeout', () => {
          meta.ending = 'timeout';
        });
        this.addEventListener(
          'loadend',
          () => {
            try {
              const request = {
                method: meta.method,
                path: meta.path,
                status: this.status || 0,
                requestId: this.getResponseHeader('x-request-id') || this.getResponseHeader('x-amzn-requestid') || ''
              };
              remember('xhr', request);
              // `timeout` 은 브라우저가 끊은 게 아니라 서버가 제때 답하지 않은 것이다.
              // 백그라운드에서 만료돼도 실제 장애이므로 보고한다.
              const cancelled = meta.ending === 'abort' || (meta.ending !== 'timeout' && isBrowserCancelledRequest(request.status));
              if (!cancelled && (this.status === 0 || (isHttpError(this.status) && !isUnauthenticatedProbe(meta.path, this.status) && !isAxiosManagedAuthResponse(this.status)))) {
                const failureKind = this.status || (meta.ending === 'timeout' ? 'timeout' : 'network');
                reportFrontendError(new Error(`${meta.method} ${meta.path} failed with HTTP ${failureKind}`), {
                  source: 'xhr',
                  request
                });
              }
            } catch {
              // XHR loadend consumer에 reporter 오류를 노출하지 않는다.
            }
          },
          { once: true }
        );
      } catch {
        // listener 설치 실패와 무관하게 원래 XHR send는 그대로 실행한다.
      }
    }
    originalSend.call(this, body ?? null);
  };
}

export function installFrontendErrorReporter(): void {
  if (typeof window === 'undefined' || installed) return;
  installed = true;
  try {
    installFetchMonitor();
  } catch {
    // 선택적 계측을 설치할 수 없는 브라우저에서도 앱은 정상 동작해야 한다.
  }
  try {
    installPageLifecycleMonitor();
  } catch {
    // 선택적 계측을 설치할 수 없는 브라우저에서도 앱은 정상 동작해야 한다.
  }
  try {
    installXhrMonitor();
  } catch {
    // 선택적 계측을 설치할 수 없는 브라우저에서도 앱은 정상 동작해야 한다.
  }
  try {
    window.addEventListener('error', (event) => {
      if (event.error) reportFrontendError(event.error, { source: 'window.error' });
    });
    window.addEventListener('unhandledrejection', (event) => {
      // fetch 계측이 이미 판정한 거절이면 여기서 끊는다 — 같은 실패의 두 번째 사본이다.
      if (wasSettledByFetchMonitor(event.reason)) return;
      reportFrontendError(event.reason, { source: 'unhandledrejection' });
    });
  } catch {
    // 선택적 계측을 설치할 수 없는 브라우저에서도 앱은 정상 동작해야 한다.
  }
}
